/**
 * Open Belot 1×1: визуальная оболочка как у `ArmenianBelotTablePresentation` + нижняя панель как в `BelotArmenianLocalLobby`.
 *
 * @module components/online/OnlineOpenBelotTable
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../poker-ui/utils/cn';
import { PokerTableSurface } from '../../poker-ui/PokerTableSurface';
import { PlayingCard } from '../../poker-ui/PlayingCard';
import { IndustrialSeat } from '../../poker-ui/IndustrialSeat';
import { useBelotOnlineDealAnimation } from './hooks/useBelotOnlineDealAnimation';
import { useBelotOnlinePlayCardFlight } from './hooks/useBelotOnlinePlayCardFlight';
import { useBelotOnlineHeroDragPlay } from './hooks/useBelotOnlineHeroDragPlay';
import { useBelotOnlineTrickEndAnimation } from './hooks/useBelotOnlineTrickEndAnimation';
import {
  belotCardColorClass,
  belotCardIdToPlaying,
  belotCardIsTrump,
  belotContractCpLabelFromEngine,
  bidCpTensForDisplay,
  formatBelotTrumpBid,
  sortBelotHandForDisplay,
} from '../../game/belotArmenianCards';
import { getSeatArenaTuning } from '../../poker-ui/seatLayout';
import {
  BELOT_ONLINE_SEAT_ELLIPSE_PRESETS,
  BELOT_ONLINE_SEAT_MOBILE_STACK_PRESETS,
  BelotHudMetricsRow,
  BelotHudToolbarButtons,
  BelotTrickFourSlots,
  getRelativeSeatPosition,
  largeTrumpDisplay,
  belotTrumpGlyphFaceClass,
  trumpBadgeToneByGlyph,
  TRICK_SLOT_TRANSFORM,
} from '../belot/BelotTableSharedUi';
import { BelotDeckTrumpIndicator } from '../belot/BelotDeckTrumpIndicator';
import './openDuellColumnFlip.css';

/** Задержка раскрытия нижней / смены верха колонки после хода (мс), совпадает с длительностью CSS flip. */
export const OPEN_DUELL_COLUMN_DEFER_MS = 420;

/** Стартовая рука в open duell (движок `dealOpenLayout`): 6+10×2. */
export const OPEN_DUELL_DEAL_HAND_SIZE = 6;

function physicalSeatForTrickSeat(seat) {
  return seat === 0 || seat === 2 ? 0 : 1;
}

function teamOfSeat(seat) {
  return seat === 0 || seat === 2 ? 0 : 1;
}

/** Как `contractPlayingPairLabel` в `BelotArmenianLocalLobby` (4 логических места). */
function contractPlayingPairLabel(contractTeam) {
  if (contractTeam === 0) return 'Юг – Север';
  if (contractTeam === 1) return 'Запад – Восток';
  return '';
}

/**
 * @param {object} props
 * @param {object|null} props.belotOpenDuell — публичный снапшот `belotOpenDuell`
 * @param {string} [props.belotPhase]
 * @param {number|null} props.mySeat
 * @param {number[]} props.myHand
 * @param {number[][]} props.myTableStacks — 5 столбцов, карты снизу вверх [нижняя, верхняя]
 * @param {{ seat: number, uid: string, name: string }[]} props.belotSeatRows
 * @param {(p: { card: number, from: 'hand'|'table', columnIndex?: number }) => void} props.onPlayCard
 * @param {boolean} props.canPlay
 * @param {number[]|null} props.legalPlayCardIds — легальные карты (рука + верхи столбцов); `null` = не подсвечивать
 * @param {string|null} props.bellaNotice — после 1-й взятки: Белла и/или комбо (`OPEN_DUELL_DECLARATIONS_RESOLVED`)
 * @param {number|null} props.heroSecLeft
 * @param {number} props.heroTurnSeconds
 * @param {number|null} [props.oppTurnDeadlineAtMs] — дедлайн хода соперника (сервер), для кольца как у бота локали
 * @param {import('../../poker-ui/AudioProvider').PlayFn} props.play
 * @param {boolean} [props.suppressed] — legacy: то же что `audioSuppressed`, если он не передан.
 * @param {boolean} [props.audioSuppressed] — mute UI/раздачи звуков (autoplay).
 * @param {boolean} [props.suppressDealAnimation] — не проигрывать раздачу (гидрация после реконнекта).
 * @param {(active: boolean) => void} [props.onRoundDealAnimActiveChange] — для блокировки ввода в родителе.
 * @param {(active: boolean) => void} [props.onPlayFlightActiveChange] — блок ввода в родителе на время полёта карты.
 * @param {number} [props.belotPlayResyncKey] — сброс локального полёта/drag при `belotSync.seq`.
 * @param {React.ReactNode} props.statusBanner
 * @param {React.ReactNode} props.bottomActions
 * @param {React.ReactNode} [props.heroAuctionPanel]
 * @param {() => void} [props.onOpenScoreSheet]
 * @param {() => void} [props.onPeekLastTrick]
 * @param {() => void} [props.onOpenEngineDebug] — полная отладка open 1×1 (кнопка «Движок»)
 * @param {React.RefObject<HTMLElement|null>} [props.contractBadgeAnchorRef]
 */
export function OnlineOpenBelotTable({
  belotOpenDuell,
  belotPhase = '',
  mySeat,
  myHand,
  myTableStacks,
  belotSeatRows,
  onPlayCard,
  canPlay,
  legalPlayCardIds = null,
  bellaNotice = null,
  heroSecLeft,
  heroTurnSeconds = 30,
  oppTurnDeadlineAtMs = null,
  play,
  suppressed = false,
  audioSuppressed: audioSuppressedProp,
  suppressDealAnimation = false,
  onRoundDealAnimActiveChange,
  onPlayFlightActiveChange,
  belotPlayResyncKey = 0,
  statusBanner,
  bottomActions,
  heroAuctionPanel = null,
  onOpenScoreSheet,
  onPeekLastTrick,
  onOpenEngineDebug,
  contractBadgeAnchorRef,
  seatArenaRef: externalSeatArenaRef = null,
  seatArrangeMode = false,
  seatOffsetBySeat = {},
  handleSeatDragPointerDown,
  handleSeatDragPointerMove,
  handleSeatDragPointerUp,
  handleSeatDragLostCapture,
}) {
  const audioSuppressed = audioSuppressedProp !== undefined ? audioSuppressedProp : suppressed;

  const deckRef = useRef(null);
  const trickCenterRef = useRef(null);
  const localSeatArenaRef = useRef(null);
  const seatArenaRef = externalSeatArenaRef || localSeatArenaRef;
  const seatNodeRefs = useRef({});
  const seatCardSlotRefs = useRef(
    Array.from({ length: 4 }, () => Array.from({ length: 8 }, () => React.createRef())),
  );
  const handSlotRefs = useMemo(() => Array.from({ length: 8 }, () => React.createRef()), []);
  const tableColumnRefs = useMemo(() => Array.from({ length: 5 }, () => React.createRef()), []);
  const flyCardWrapRef = useRef(null);
  const flyTweenRef = useRef(null);
  const pendingPayloadRef = useRef(null);
  const playFlightLockRef = useRef(false);
  const prevHeroStacksRef = useRef(null);
  const heroStacksInitRef = useRef(false);
  const prevOppColsRef = useRef(null);
  const oppColsInitRef = useRef(false);
  const heroDeferTimersRef = useRef(/** @type {Record<number, number>} */ ({}));
  const oppDeferTimerRef = useRef(null);

  const [mountSuppress, setMountSuppress] = useState(true);
  const [roundDealAnimActive, setRoundDealAnimActive] = useState(false);
  const [seatDealSlotRevealed, setSeatDealSlotRevealed] = useState(null);

  const setRoundDealAnimActiveWrapped = useCallback(
    (v) => {
      setRoundDealAnimActive(v);
      onRoundDealAnimActiveChange?.(v);
    },
    [onRoundDealAnimActiveChange],
  );
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [compactHandLayout, setCompactHandLayout] = useState(false);
  /** @type {[{ col: number, bottom: number, until: number } | null, (v: any) => void]} */
  const [heroColDefer, setHeroColDefer] = useState(null);
  /** @type {[{ col: number, until: number } | null, (v: any) => void]} */
  const [, setOppTopDefer] = useState(null);
  const [, bumpRerender] = useState(0);

  const [cardFly, setCardFly] = useState(null);
  /** @type {[null | { from: 'hand'|'table', card: number, columnIndex?: number }, (v: any) => void]} */
  const [pendingHiddenPlay, setPendingHiddenPlay] = useState(null);
  /** @type {[null | { plays: { seat: number, card: number }[], winnerSeat: number|null, token: number }, (v: any) => void]} */
  const [trickEndOverlay, setTrickEndOverlay] = useState(null);

  const [arenaCompact, setArenaCompact] = useState(false);
  /** Перерисовка кольца соперника (~50 Гц), как `oppRingTick` в локале. */
  const [oppRingTick, setOppRingTick] = useState(0);
  /** Актуальный дедлайн для тика интервала (props в замыкании устаревают). */
  const oppDeadlineMsRef = useRef(oppTurnDeadlineAtMs);
  oppDeadlineMsRef.current = oppTurnDeadlineAtMs;
  const seatArena = useMemo(() => getSeatArenaTuning(4), []);

  useLayoutEffect(() => {
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setMountSuppress(false);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 640px)');
    const apply = () => setCompactHandLayout(Boolean(mq.matches));
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setPrefersReducedMotion(Boolean(mq.matches));
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 820px)');
    const apply = () => setArenaCompact(Boolean(mq.matches));
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const legalSet = useMemo(() => {
    if (!Array.isArray(legalPlayCardIds)) return null;
    return new Set(legalPlayCardIds);
  }, [legalPlayCardIds]);

  const contractTrump = belotOpenDuell?.contract?.trumpSuit ?? null;
  const currentTrick = useMemo(
    () => (Array.isArray(belotOpenDuell?.currentTrick) ? belotOpenDuell.currentTrick : []),
    [belotOpenDuell?.currentTrick],
  );
  const currentTrickLen = currentTrick.length;

  const oppSeat = mySeat === 1 ? 0 : 1;
  const oppCols = useMemo(() => {
    const t = belotOpenDuell?.tableColumnsPublic;
    if (!Array.isArray(t?.[oppSeat])) return [];
    return t[oppSeat];
  }, [belotOpenDuell?.tableColumnsPublic, oppSeat]);

  const hasHeroTableCards = useMemo(() => {
    if (!Array.isArray(myTableStacks)) return false;
    return myTableStacks.some((s) => Array.isArray(s) && s.length > 0);
  }, [myTableStacks]);

  const sortedHand = useMemo(() => sortBelotHandForDisplay(myHand || [], contractTrump), [myHand, contractTrump]);

  const openHandFanMetrics = useMemo(() => {
    const handCount = sortedHand.length;
    const mobileStack = compactHandLayout;
    const baseFanStep = mobileStack ? (handCount > 7 ? 36 : 41) : handCount > 7 ? 34 : 40;
    const desiredFanWidth = Math.max(mobileStack ? 268 : 240, (handCount - 1) * baseFanStep + 84);
    const maxFanWidth = mobileStack ? 368 : 340;
    const fanWidth = Math.min(desiredFanWidth, maxFanWidth);
    const fanStep = handCount > 1 ? (fanWidth - 84) / (handCount - 1) : 0;
    const fanBaseYOffset = mobileStack ? -38 : -50;
    return { handCount, fanWidth, fanStep, fanBaseYOffset, compactFan: mobileStack };
  }, [sortedHand.length, compactHandLayout]);

  const teamScores = belotOpenDuell?.teamScores;
  const s0 = Array.isArray(teamScores) ? Number(teamScores[0]) || 0 : 0;
  const s1 = Array.isArray(teamScores) ? Number(teamScores[1]) || 0 : 0;
  const targetScore = Number(belotOpenDuell?.targetScore) || 10010;
  const lastCompleted = belotOpenDuell?.lastCompletedTrick;

  const tricksTakenByTeam = useMemo(() => {
    const t = belotOpenDuell?.tricksTakenByTeam;
    if (!Array.isArray(t)) return [0, 0];
    return [Number(t[0]) || 0, Number(t[1]) || 0];
  }, [belotOpenDuell?.tricksTakenByTeam]);

  const turnSeatName = useMemo(() => {
    const s = belotOpenDuell?.currentTurnSeat;
    if (typeof s !== 'number') return '';
    const phys = physicalSeatForTrickSeat(s);
    return belotSeatRows?.[phys]?.name || `Игрок ${phys}`;
  }, [belotOpenDuell?.currentTurnSeat, belotSeatRows]);

  const auctionStatusFallback = useMemo(() => {
    if (belotPhase !== 'auction' || !belotOpenDuell) return null;
    if (typeof mySeat === 'number' && belotOpenDuell.currentTurnSeat === mySeat) return 'Ваш ход в торгах.';
    if (turnSeatName) return `Торги · ход: ${turnSeatName}`;
    return 'Торги';
  }, [belotPhase, belotOpenDuell, mySeat, turnSeatName]);

  const tableStatusContent = statusBanner ?? auctionStatusFallback;
  const topNoticeContent =
    bellaNotice && tableStatusContent ? `${bellaNotice} · ${tableStatusContent}` : bellaNotice || tableStatusContent;

  const isHeroAuctionTurn =
    belotPhase === 'auction' &&
    typeof mySeat === 'number' &&
    belotOpenDuell?.currentTurnSeat === mySeat;

  const canPeekLastTrick =
    belotPhase === 'play' &&
    currentTrick.length === 0 &&
    Array.isArray(lastCompleted?.plays) &&
    lastCompleted.plays.length === 4;

  const heroTimerShow =
    belotPhase === 'play' && canPlay && heroSecLeft != null && Number.isFinite(heroSecLeft);

  const clearHeroDeferTimer = useCallback((col) => {
    const t = heroDeferTimersRef.current[col];
    if (t != null) window.clearTimeout(t);
    delete heroDeferTimersRef.current[col];
  }, []);

  const openRoundIndex = belotOpenDuell?.roundIndex;
  /** Сброс диффа стопок при новой раздаче (без ложного defer между раундами). */
  useEffect(() => {
    for (const k of Object.keys(heroDeferTimersRef.current)) {
      window.clearTimeout(heroDeferTimersRef.current[Number(k)]);
    }
    heroDeferTimersRef.current = {};
    if (oppDeferTimerRef.current != null) {
      window.clearTimeout(oppDeferTimerRef.current);
      oppDeferTimerRef.current = null;
    }
    setHeroColDefer(null);
    setOppTopDefer(null);
    prevHeroStacksRef.current = myTableStacks;
    heroStacksInitRef.current = false;
    prevOppColsRef.current = oppCols;
    oppColsInitRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только смена раунда
  }, [openRoundIndex]);

  useEffect(() => {
    return () => {
      for (const k of Object.keys(heroDeferTimersRef.current)) {
        window.clearTimeout(heroDeferTimersRef.current[Number(k)]);
      }
      heroDeferTimersRef.current = {};
      if (oppDeferTimerRef.current != null) {
        window.clearTimeout(oppDeferTimerRef.current);
        oppDeferTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const stacks = myTableStacks;
    if (!heroStacksInitRef.current) {
      heroStacksInitRef.current = true;
      prevHeroStacksRef.current = stacks;
      return;
    }
    const prev = prevHeroStacksRef.current;
    prevHeroStacksRef.current = stacks;
    if (!Array.isArray(prev) || !Array.isArray(stacks)) return;

    for (let c = 0; c < 5; c += 1) {
      const po = Array.isArray(prev[c]) ? prev[c] : [];
      const cr = Array.isArray(stacks[c]) ? stacks[c] : [];
      if (po.length >= 2 && cr.length === po.length - 1) {
        const oldTop = po[po.length - 1];
        const oldBot = po[0];
        const newTop = cr[cr.length - 1];
        if (cr.length >= 1 && newTop === oldBot && oldTop !== newTop) {
          clearHeroDeferTimer(c);
          setHeroColDefer({ col: c, bottom: oldBot, until: Date.now() + OPEN_DUELL_COLUMN_DEFER_MS });
          if (!audioSuppressed) {
            try {
              play?.('card-flip', 0.42);
            } catch {
              /* ignore */
            }
          }
          heroDeferTimersRef.current[c] = window.setTimeout(() => {
            setHeroColDefer((cur) => (cur && cur.col === c ? null : cur));
            delete heroDeferTimersRef.current[c];
            bumpRerender((x) => x + 1);
          }, OPEN_DUELL_COLUMN_DEFER_MS);
        }
      }
    }
  }, [myTableStacks, clearHeroDeferTimer, play, audioSuppressed]);

  useEffect(() => {
    if (!oppColsInitRef.current) {
      oppColsInitRef.current = true;
      prevOppColsRef.current = oppCols;
      return;
    }
    const prev = prevOppColsRef.current;
    prevOppColsRef.current = oppCols;
    if (!Array.isArray(prev) || !Array.isArray(oppCols)) return;
    const n = Math.min(prev.length, oppCols.length);
    for (let i = 0; i < n; i += 1) {
      const pt = prev[i]?.topCard;
      const ct = oppCols[i]?.topCard;
      if (pt != null && ct != null && pt !== ct) {
        if (oppDeferTimerRef.current != null) window.clearTimeout(oppDeferTimerRef.current);
        setOppTopDefer({ col: i, until: Date.now() + OPEN_DUELL_COLUMN_DEFER_MS });
        oppDeferTimerRef.current = window.setTimeout(() => {
          oppDeferTimerRef.current = null;
          setOppTopDefer(null);
          bumpRerender((x) => x + 1);
        }, OPEN_DUELL_COLUMN_DEFER_MS);
        break;
      }
    }
  }, [oppCols]);

  const rowNameForTrickSeat = useCallback(
    (seat) => {
      const phys = physicalSeatForTrickSeat(seat);
      return belotSeatRows?.[phys]?.name || `Игрок ${phys}`;
    },
    [belotSeatRows],
  );

  const heroDeferActive = heroColDefer && Date.now() < heroColDefer.until ? heroColDefer : null;

  const heroSeatAnchor = typeof mySeat === 'number' && mySeat >= 0 ? mySeat : 0;
  const tricksPot = tricksTakenByTeam[0] + tricksTakenByTeam[1];

  const { handCount, fanWidth, fanStep, fanBaseYOffset, compactFan } = openHandFanMetrics;

  useBelotOnlineDealAnimation({
    belotArmenian: belotOpenDuell,
    deckRef,
    seatRefs: seatNodeRefs,
    handSlotRefs,
    seatCardSlotRefs: seatCardSlotRefs.current,
    play,
    audioSuppressed,
    suppressDealAnimation,
    cardsPerSeatForDeal: OPEN_DUELL_DEAL_HAND_SIZE,
    heroSeat: heroSeatAnchor,
    heroHand: myHand,
    setRoundDealAnimActive: setRoundDealAnimActiveWrapped,
    setSeatDealSlotRevealed,
  });

  const commitHeroPlay = useCallback(
    (/** @type {{ card: number, from: 'hand'|'table', columnIndex?: number }} */ payload, dragDropFromPoint) => {
      if (belotPhase !== 'play' || !canPlay) return;
      if (playFlightLockRef.current) return;
      const trickEl = trickCenterRef.current;
      const reduced =
        typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (!trickEl || reduced) {
        onPlayCard(payload);
        return;
      }
      let fromEl = null;
      if (payload.from === 'hand') {
        const i = sortedHand.indexOf(payload.card);
        fromEl = i >= 0 ? handSlotRefs[i]?.current : null;
      } else if (payload.from === 'table' && Number.isInteger(payload.columnIndex)) {
        fromEl = tableColumnRefs[payload.columnIndex]?.current;
      }
      if (!fromEl) {
        onPlayCard(payload);
        return;
      }
      const { rank, suit } = belotCardIdToPlaying(payload.card);
      const fr = fromEl.getBoundingClientRect();
      const tr = trickEl.getBoundingClientRect();
      playFlightLockRef.current = true;
      onPlayFlightActiveChange?.(true);
      pendingPayloadRef.current = payload;
      if (payload.from === 'table' && Number.isInteger(payload.columnIndex)) {
        setPendingHiddenPlay({
          from: 'table',
          card: payload.card,
          columnIndex: payload.columnIndex,
        });
      } else {
        setPendingHiddenPlay({ from: 'hand', card: payload.card });
      }
      const startX = dragDropFromPoint?.x ?? fr.left + fr.width / 2;
      const startY = dragDropFromPoint?.y ?? fr.top + fr.height / 2;
      setCardFly({
        key: Date.now(),
        x0: startX,
        y0: startY,
        x1: tr.left + tr.width / 2,
        y1: tr.top + tr.height / 2,
        rank,
        suit,
        fromDrag: Boolean(dragDropFromPoint),
      });
    },
    [belotPhase, canPlay, onPlayCard, sortedHand, handSlotRefs, tableColumnRefs, onPlayFlightActiveChange],
  );

  useBelotOnlinePlayCardFlight({
    cardFly,
    setCardFly,
    flyCardWrapRef,
    flyTweenRef,
    trickCenterRef,
    pendingPayloadRef,
    onSend: onPlayCard,
    onFlightEnd: () => {
      playFlightLockRef.current = false;
      onPlayFlightActiveChange?.(false);
    },
  });

  const {
    dragPlayCard,
    dragGhostRef,
    onPlayPointerDown,
    onPlayPointerMove,
    onPlayPointerUp,
    onPlayPointerCancel,
    onPlayCardClick,
    clearDragPlayCard,
  } = useBelotOnlineHeroDragPlay({
    trickCenterRef,
    enabled: belotPhase === 'play' && !roundDealAnimActive,
    isInputBlocked: !canPlay || Boolean(cardFly),
    cardIdToPlaying: belotCardIdToPlaying,
    onCommitPlay: commitHeroPlay,
  });

  useBelotOnlineTrickEndAnimation({
    belotPhase,
    roundIndex: Number(belotOpenDuell?.roundIndex) || 0,
    currentTrickLen,
    lastCompletedTrick: lastCompleted,
    setTrickEndOverlay,
  });

  useEffect(() => {
    flyTweenRef.current?.kill();
    flyTweenRef.current = null;
    pendingPayloadRef.current = null;
    setCardFly(null);
    setPendingHiddenPlay(null);
    playFlightLockRef.current = false;
    clearDragPlayCard();
    onPlayFlightActiveChange?.(false);
  }, [belotPlayResyncKey, clearDragPlayCard, onPlayFlightActiveChange]);

  useEffect(() => {
    if (!pendingHiddenPlay) return;
    if (pendingHiddenPlay.from === 'hand') {
      if (!sortedHand.includes(pendingHiddenPlay.card)) setPendingHiddenPlay(null);
      return;
    }
    const col = pendingHiddenPlay.columnIndex;
    const st = Array.isArray(myTableStacks) ? myTableStacks[col] : null;
    const top = Array.isArray(st) && st.length ? st[st.length - 1] : null;
    if (top !== pendingHiddenPlay.card) setPendingHiddenPlay(null);
  }, [sortedHand, myTableStacks, pendingHiddenPlay]);

  const currentAuctionBid = belotOpenDuell?.auction?.highestBid ?? null;
  const auctionBidAuthorLabel = useMemo(() => {
    const seat = belotOpenDuell?.auction?.bidAuthorSeat;
    if (seat == null) return null;
    const s = Number(seat);
    if (!Number.isInteger(s) || (s !== 0 && s !== 1)) return null;
    const row = Array.isArray(belotSeatRows) ? belotSeatRows.find((r) => Number(r.seat) === s) : null;
    return row?.name || `Место ${s}`;
  }, [belotOpenDuell?.auction?.bidAuthorSeat, belotSeatRows]);
  const contract = belotOpenDuell?.contract;
  const contractTeamLabel =
    contract && typeof contract.contractTeam === 'number'
      ? contractPlayingPairLabel(contract.contractTeam)
      : '';
  const dealerSeat = typeof belotOpenDuell?.dealerSeat === 'number' ? belotOpenDuell.dealerSeat : null;
  const turnSeat = typeof belotOpenDuell?.currentTurnSeat === 'number' ? belotOpenDuell.currentTurnSeat : null;
  const inAuction = belotPhase === 'auction';
  const inPlay = belotPhase === 'play';

  useEffect(() => {
    const turnPhys = typeof turnSeat === 'number' ? physicalSeatForTrickSeat(turnSeat) : null;
    const heroPhys = typeof mySeat === 'number' ? mySeat : null;
    const endAt = Number(oppTurnDeadlineAtMs);
    const deadlineLive = Number.isFinite(endAt) && Date.now() < endAt;
    const isOppTurn =
      (inAuction || inPlay) &&
      turnPhys != null &&
      heroPhys != null &&
      turnPhys !== heroPhys &&
      deadlineLive;
    if (!isOppTurn) return undefined;
    const iv = window.setInterval(() => {
      const end = Number(oppDeadlineMsRef.current);
      if (!Number.isFinite(end) || Date.now() >= end) {
        window.clearInterval(iv);
        return;
      }
      setOppRingTick((x) => x + 1);
    }, 50);
    return () => window.clearInterval(iv);
  }, [turnSeat, mySeat, inAuction, inPlay, oppTurnDeadlineAtMs]);

  const opponentTurnFraction = useMemo(() => {
    void oppRingTick;
    if (!Number.isFinite(Number(oppTurnDeadlineAtMs))) return null;
    const turnPhys = typeof turnSeat === 'number' ? physicalSeatForTrickSeat(turnSeat) : null;
    const heroPhys = typeof mySeat === 'number' ? mySeat : null;
    if (turnPhys == null || heroPhys == null || turnPhys === heroPhys) return null;
    if (!(inAuction || inPlay)) return null;
    const endAt = Number(oppTurnDeadlineAtMs);
    const now = Date.now();
    if (now >= endAt) return null;
    const totalMs = Math.max(1, heroTurnSeconds * 1000);
    return Math.max(0, Math.min(1, (endAt - now) / totalMs));
  }, [
    oppRingTick,
    oppTurnDeadlineAtMs,
    turnSeat,
    mySeat,
    inAuction,
    inPlay,
    heroTurnSeconds,
  ]);

  const heroIndex = heroSeatAnchor;

  const seatsForRender = useMemo(() => {
    const seatPresets = arenaCompact ? BELOT_ONLINE_SEAT_MOBILE_STACK_PRESETS : BELOT_ONLINE_SEAT_ELLIPSE_PRESETS;
    const heroPhysSeat = heroIndex === 1 ? 1 : 0;
    const oppPhysSeat = heroPhysSeat === 0 ? 1 : 0;
    return [heroPhysSeat, oppPhysSeat].map((phys) => {
      const visualOffset = phys === heroPhysSeat ? 0 : 2;
      const slot = seatPresets[visualOffset];
      const row = belotSeatRows?.[phys];
      const isViewer = typeof mySeat === 'number' && phys === mySeat;
      const handSize =
        isViewer && Array.isArray(myHand)
          ? myHand.length
          : Math.max(0, Number(belotOpenDuell?.handCounts?.[phys]) || (belotPhase === 'play' ? 0 : 6));
      return { seatKey: `phys-${phys}`, slot, row, handSize, isViewer, phys };
    });
  }, [arenaCompact, belotSeatRows, mySeat, myHand, belotOpenDuell?.handCounts, belotPhase, heroIndex]);

  return (
    <div
      className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden"
      data-open-duell-mount-suppress={mountSuppress ? '1' : undefined}
    >
      {belotPhase === 'auction' && belotOpenDuell ? (
        <div className="sticky top-0 z-[25] shrink-0 border-b border-border/50 bg-card/88 px-2 py-1.5 shadow-sm backdrop-blur-md sm:px-3 sm:py-2">
          <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Текущий контракт</p>
              <p className="mt-0.5 truncate text-xs font-medium leading-snug text-foreground sm:text-[13px]">
                {currentAuctionBid ? (
                  <>
                    {auctionBidAuthorLabel ? (
                      <span className="font-black text-amber-100">{auctionBidAuthorLabel}:</span>
                    ) : (
                      <span className="font-black text-amber-100">Заявка:</span>
                    )}{' '}
                    <span className="font-mono text-amber-200/95">{bidCpTensForDisplay(currentAuctionBid)} CP</span>{' '}
                    <span>{formatBelotTrumpBid(currentAuctionBid.trump)}</span>
                    {Number(belotOpenDuell.auction?.contraMultiplier) > 1 ? (
                      <span className="ml-1 font-mono text-xs text-cyan-300/90">
                        ×{Number(belotOpenDuell.auction.contraMultiplier)}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className="text-muted-foreground">Открытие от 10 CP</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {(() => {
                const L = currentAuctionBid ? largeTrumpDisplay(currentAuctionBid.trump) : { line1: '—', line2: null };
                return (
                  <div
                    ref={contractBadgeAnchorRef}
                    className={cn(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-black/30 text-xl font-bold leading-none shadow-inner sm:h-11 sm:w-11 sm:text-2xl',
                      trumpBadgeToneByGlyph(L.line1),
                    )}
                    title={L.line2 || undefined}
                    aria-hidden
                  >
                    <span className={belotTrumpGlyphFaceClass(L.line1)}>{L.line1}</span>
                  </div>
                );
              })()}
              <button
                type="button"
                disabled
                title="Лог торгов в онлайн-Open пока недоступен"
                className="rounded-md border border-border/55 bg-transparent px-2 py-1 text-[10px] font-medium text-muted-foreground/50 sm:text-[11px]"
              >
                Лог торгов
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {belotPhase === 'play' && contract ? (
        <div className="sticky top-0 z-[25] shrink-0 border-b border-border/50 bg-card/88 px-3 py-2 shadow-sm backdrop-blur-md">
          <div className="mx-auto flex max-w-2xl items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Контракт</p>
              <p className="mt-0.5 truncate text-xs font-medium leading-snug text-foreground sm:text-[13px]">
                <span className="font-mono tabular-nums text-amber-200/95">
                  {belotContractCpLabelFromEngine(contract)} CP
                </span>
                <span className="text-muted-foreground/80"> · </span>
                <span>{formatBelotTrumpBid(contract.trumpSuit)}</span>
                {contract.kapot ? (
                  <>
                    <span className="text-muted-foreground/80"> · </span>
                    <span className="rounded border border-amber-500/35 bg-amber-950/20 px-1 py-px text-[10px] font-semibold uppercase text-amber-100/90">
                      капот
                    </span>
                  </>
                ) : null}
                {contractTeamLabel ? (
                  <>
                    <span className="text-muted-foreground/80"> · </span>
                    <span className="text-muted-foreground">{contractTeamLabel}</span>
                  </>
                ) : null}
                {Number(contract.contraMultiplier) > 1 ? (
                  <span className="ml-1 font-mono text-xs text-cyan-300/90">×{Number(contract.contraMultiplier)}</span>
                ) : null}
              </p>
            </div>
            {(() => {
              const L = largeTrumpDisplay(contract.trumpSuit);
              return (
                <div
                  ref={contractBadgeAnchorRef}
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-black/30 text-xl font-bold leading-none shadow-inner sm:h-11 sm:w-11 sm:text-2xl',
                    trumpBadgeToneByGlyph(L.line1),
                  )}
                  title={L.line2 || undefined}
                  aria-hidden
                >
                  <span className={belotTrumpGlyphFaceClass(L.line1)}>{L.line1}</span>
                </div>
              );
            })()}
          </div>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-1 sm:px-3 sm:py-2">
        <BelotHudMetricsRow
          teamScores={[s0, s1]}
          tricksWonByTeam={tricksTakenByTeam}
          roundIndex={Number(belotOpenDuell?.roundIndex) || 0}
          targetScore={targetScore}
        >
          {(mySeat != null && (onOpenScoreSheet || onPeekLastTrick)) || onOpenEngineDebug ? (
            <BelotHudToolbarButtons
              onOpenScoreSheet={onOpenScoreSheet}
              onOpenEngineDebug={onOpenEngineDebug}
              onPeekLastTrick={onPeekLastTrick || (() => {})}
              canPeekLastTrick={Boolean(canPeekLastTrick && onPeekLastTrick)}
              heroSecLeft={heroTimerShow ? Math.max(0, Math.ceil(heroSecLeft)) : null}
              heroTimedTurn={Boolean(heroTimerShow)}
            />
          ) : null}
        </BelotHudMetricsRow>

        {topNoticeContent ? (
          <div
            className={cn(
              'mx-auto mt-0 mb-2 max-w-2xl rounded-xl border px-3 py-1.5 text-center text-[11px] font-semibold shadow-inner sm:mb-3',
              belotPhase === 'auction'
                ? isHeroAuctionTurn
                  ? 'border-neon-cyan/35 bg-card/70 text-neon-cyan'
                  : 'border-amber-500/40 bg-amber-950/25 text-amber-100'
                : belotPhase === 'play'
                  ? canPlay
                    ? 'border-neon-cyan/35 bg-card/70 text-neon-cyan'
                    : 'border-border/60 bg-card/60 text-muted-foreground'
                  : 'border-border/60 bg-card/60 text-muted-foreground',
            )}
          >
            {topNoticeContent}
          </div>
        ) : null}

        <div
          ref={seatArenaRef}
          className={cn(
            'relative mx-auto mt-0 w-full flex-1',
            seatArena.maxWidthClass,
            seatArena.minHeightClass,
          )}
        >
          <div className="pointer-events-none absolute inset-0 z-[8]">
            {seatsForRender.map(({ seatKey, slot, row, handSize, isViewer, phys }) => {
              const turnPhys = typeof turnSeat === 'number' ? physicalSeatForTrickSeat(turnSeat) : null;
              const dealerPhys = typeof dealerSeat === 'number' ? physicalSeatForTrickSeat(dealerSeat) : null;
              const isCurrentTurn = (inAuction || inPlay) && turnPhys === phys;
              const tricksForTeam = Number(tricksTakenByTeam[teamOfSeat(phys)] ?? 0);
              const seatName = (row?.name || (row?.uid ? `id …${String(row.uid).slice(-4)}` : `Игрок ${phys}`)).slice(0, 12);
              const revealedRow = seatDealSlotRevealed?.[phys];
              const oppBackCap =
                belotPhase === 'auction' ? Math.min(OPEN_DUELL_DEAL_HAND_SIZE, 8) : 3;
              const visibleSeatCardCount = !isViewer
                ? Math.max(0, Math.min(oppBackCap, Number(handSize) || 0))
                : 0;
              const seatCards = !isViewer ? Array.from({ length: visibleSeatCardCount }, (_, i) => i) : [];
              const heroTurnTimerSeat =
                isViewer &&
                turnPhys === phys &&
                heroSecLeft != null &&
                Number.isFinite(heroSecLeft) &&
                (inAuction || (inPlay && canPlay));
              const dispLeft = Number(slot.leftPct) + (Number(seatOffsetBySeat?.[phys]?.dLeft) || 0);
              const dispTop = Number(slot.topPct) + (Number(seatOffsetBySeat?.[phys]?.dTop) || 0);
              return (
                <div
                  key={`open-seat-${seatKey}`}
                  className="absolute"
                  style={{
                    left: `${dispLeft}%`,
                    top: `${dispTop}%`,
                    transform: 'translate(-50%, -50%)',
                    transformOrigin: 'center',
                  }}
                >
                  <div
                    ref={(el) => {
                      seatNodeRefs.current[phys] = el;
                    }}
                    className={cn(
                      'pointer-events-auto relative flex flex-col items-center gap-0.5 transition-all duration-250',
                      seatArrangeMode && 'cursor-grab touch-none active:cursor-grabbing',
                    )}
                    onPointerDown={(ev) => handleSeatDragPointerDown?.(ev, phys, slot.leftPct, slot.topPct)}
                    onPointerMove={handleSeatDragPointerMove}
                    onPointerUp={handleSeatDragPointerUp}
                    onPointerCancel={handleSeatDragPointerUp}
                    onLostPointerCapture={handleSeatDragLostCapture}
                  >
                    <IndustrialSeat
                      name={seatName}
                      stackMetaVertically
                      boostSeatAvatar
                      chipsDisplay={null}
                      chips={null}
                      seatColorIndex={phys}
                      isCurrentTurn={isCurrentTurn}
                      isUser={isViewer}
                      isBot={false}
                      compact
                      avatarUrl={row?.avatarUrl || ''}
                      isDealer={dealerPhys === phys}
                      showCards={!isViewer}
                      cards={seatCards}
                      cardFaceDownByIndex={!isViewer ? seatCards.map(() => true) : undefined}
                      showCardByIndex={
                        !isViewer
                          ? seatCards.map((_, i) => (roundDealAnimActive ? Boolean(revealedRow?.[i]) : true))
                          : undefined
                      }
                      cardSlotRefs={!isViewer ? seatCardSlotRefs.current[phys] : undefined}
                      turnTimeLeftSec={heroTurnTimerSeat ? Math.max(0, Math.ceil(heroSecLeft)) : null}
                      turnTimeTotalSec={heroTurnTimerSeat ? heroTurnSeconds : null}
                      turnTimeFraction={
                        !isViewer && isCurrentTurn && opponentTurnFraction != null ? opponentTurnFraction : null
                      }
                      suppressAvatarDragPropagation={seatArrangeMode}
                    >
                      {tricksForTeam > 0 && canPeekLastTrick && onPeekLastTrick ? (
                        <button
                          type="button"
                          onClick={onPeekLastTrick}
                          className="rounded border border-white/15 bg-black/20 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-white/75"
                        >
                          🃏 {tricksForTeam}
                        </button>
                      ) : null}
                    </IndustrialSeat>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="absolute inset-0 flex items-center justify-center py-0.5">
            <PokerTableSurface
              ref={deckRef}
              communitySlots={[null, null, null, null, null]}
              pot={tricksPot}
              potLabel="ВЗЯТКИ"
              showCommunity={false}
              showDeck
              deckPulseKey={Number(belotOpenDuell?.roundIndex) || 0}
              potAnchorRef={trickCenterRef}
              deckLayerExtra={
                <BelotDeckTrumpIndicator
                  enabled
                  phase={belotPhase}
                  contract={contract}
                  roundIndex={Number(belotOpenDuell?.roundIndex) || 0}
                  auctionRevealMs={2350}
                  prefersReducedMotion={prefersReducedMotion}
                  cardIdToPlaying={belotCardIdToPlaying}
                />
              }
            />
          </div>
          {dragPlayCard && belotPhase === 'play' ? (
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-[19] -translate-x-1/2 -translate-y-1/2">
              <div
                className={cn(
                  'h-[10.8rem] w-24 max-h-[66vmin] max-w-[48vmin] shrink-0 rounded-[50%] border-2 transition-all duration-100',
                  dragPlayCard.dropActive
                    ? 'border-cyan-300/95 bg-cyan-500/18 shadow-[0_0_28px_rgba(34,211,238,0.5)]'
                    : 'border-cyan-400/55 bg-cyan-500/10 shadow-[0_0_18px_rgba(34,211,238,0.26)]',
                )}
              />
            </div>
          ) : null}
          <BelotTrickFourSlots
            plays={currentTrick}
            heroSeatAnchor={heroSeatAnchor}
            cardIdToPlaying={belotCardIdToPlaying}
            contractTrump={contractTrump}
            seatLabel={rowNameForTrickSeat}
            showEmptySlots={false}
          />
          {trickEndOverlay?.plays?.length === 4 ? (
            <div className="pointer-events-none absolute inset-0 z-[22]">
              {trickEndOverlay.plays.map((entry, idx) => {
                const seat = typeof entry?.seat === 'number' ? entry.seat : idx;
                const card = entry?.card;
                const position = getRelativeSeatPosition(seat, heroSeatAnchor);
                const isTr = belotCardIsTrump(contractTrump, card);
                const { rank, suit } = belotCardIdToPlaying(card);
                const isWinningReveal =
                  trickEndOverlay.winnerSeat != null && trickEndOverlay.winnerSeat === seat;
                return (
                  <div
                    key={`open-trick-end-${trickEndOverlay.token}-${seat}-${idx}`}
                    className="absolute left-1/2 top-1/2"
                    style={{ transform: TRICK_SLOT_TRANSFORM[position], zIndex: 10 + idx }}
                  >
                    <div className="relative">
                      <PlayingCard
                        rank={rank}
                        suit={suit}
                        size="sm"
                        className={cn(
                          'shadow-2xl',
                          isTr && 'ring-2 ring-amber-300/55',
                          isWinningReveal && 'ring-2 ring-neon-gold ring-offset-1 ring-offset-black/35',
                        )}
                      />
                      <span className="absolute -bottom-4 left-1/2 max-w-[5rem] -translate-x-1/2 truncate whitespace-nowrap rounded bg-black/55 px-1 py-0.5 text-[9px] text-white/80">
                        {rowNameForTrickSeat(seat)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        {heroAuctionPanel ? (
          <div className="shrink-0 border-t border-border/40 bg-background/95 px-1.5 pb-0 pt-1 sm:px-2 sm:pt-1.5">
            {heroAuctionPanel}
          </div>
        ) : null}

        {handCount > 0 ? (
          <div className={heroAuctionPanel ? 'pt-0' : 'pt-0.5'}>
            <div
              className={cn(
                'rounded-xl border border-neon-cyan/25 bg-card/55',
                compactFan ? 'p-1 sm:p-1.5' : 'p-1.5 sm:p-2',
              )}
            >
              <div
                className={cn(
                  'flex min-h-0 items-stretch justify-center overflow-hidden pb-0 pt-1 sm:pt-2',
                  compactFan
                    ? 'h-[198px] sm:h-[220px] md:h-[236px]'
                    : 'h-[168px] sm:h-[184px] md:h-[196px]',
                )}
              >
                <div
                  className="relative h-full min-h-0 min-w-0 [filter:drop-shadow(0_0_15px_rgba(0,255,255,0.35))]"
                  style={{ width: `${fanWidth}px` }}
                >
                  {inPlay &&
                  canPlay &&
                  !roundDealAnimActive &&
                  sortedHand.some((id) => legalSet?.has?.(id)) ? (
                    <div
                      className={cn(
                        'pointer-events-none absolute left-1/2 bottom-0 z-[1] -translate-x-1/2 translate-y-1 rounded-[50%] border-2 border-neon-gold/50 bg-gradient-to-t from-neon-gold/14 via-neon-gold/5 to-transparent shadow-[0_0_32px_rgba(255,215,0,0.22)]',
                        compactFan
                          ? 'h-[6.5rem] w-[min(100%,17rem)] max-w-[94%]'
                          : 'h-[7.35rem] w-[min(100%,19rem)] max-w-[92%]',
                      )}
                      aria-hidden
                    />
                  ) : null}
                  {sortedHand.map((cardId, idx) => {
                    if (roundDealAnimActive && sortedHand.length === OPEN_DUELL_DEAL_HAND_SIZE) {
                      const slotRevealed = Boolean(seatDealSlotRevealed?.[heroIndex]?.[idx]);
                      const centerOffset = idx - (handCount - 1) / 2;
                      const spreadFactor = handCount > 1 ? centerOffset / ((handCount - 1) / 2) : 0;
                      const rotate = spreadFactor * (compactFan ? 14 : 18);
                      const curveY = Math.abs(spreadFactor) * (compactFan ? 14 : 20);
                      const translateX = centerOffset * fanStep;
                      return (
                        <div
                          key={`open-deal-slot-${idx}`}
                          ref={handSlotRefs[idx]}
                          className="absolute bottom-0 left-1/2"
                          style={{
                            zIndex: idx + 1,
                            transform: `translateX(calc(-50% + ${translateX}px)) translateY(${fanBaseYOffset + curveY}px) rotate(${rotate}deg)`,
                          }}
                          aria-hidden
                        >
                          {slotRevealed ? (
                            <div className="pointer-events-none">
                              <PlayingCard faceDown size="md" />
                            </div>
                          ) : (
                            <div className="pointer-events-none invisible h-[5.5rem] w-16 shrink-0" />
                          )}
                        </div>
                      );
                    }
                    const { rank, suit } = belotCardIdToPlaying(cardId);
                    const handLegal = Boolean(legalSet?.has(cardId));
                    const canPlayCard = Boolean(canPlay && handLegal);
                    const centerOffset = idx - (handCount - 1) / 2;
                    const spreadFactor = handCount > 1 ? centerOffset / ((handCount - 1) / 2) : 0;
                    const rotate = spreadFactor * (compactFan ? 14 : 18);
                    const curveY = Math.abs(spreadFactor) * (compactFan ? 14 : 20);
                    const translateX = centerOffset * fanStep;
                    const liftY = hoveredCardIndex === idx && canPlayCard ? (compactFan ? 16 : 22) : 0;
                    const baseScale = canPlayCard ? (compactFan ? 1.1 : 1.2) : 1;
                    const scale =
                      hoveredCardIndex === idx && canPlayCard
                        ? compactFan
                          ? 1.14
                          : 1.24
                        : baseScale;
                    const baseZIndex = canPlayCard ? 80 + idx : idx + 1;
                    const tf = `translateX(calc(-50% + ${translateX}px)) translateY(${fanBaseYOffset + curveY - liftY}px) rotate(${rotate}deg) scale(${scale})`;
                    const isPendingHidden =
                      pendingHiddenPlay?.from === 'hand' && pendingHiddenPlay.card === cardId;
                    const playPayload = { card: cardId, from: /** @type {const} */ ('hand') };

                    return (
                      <button
                        key={`${cardId}-${idx}`}
                        ref={handSlotRefs[idx]}
                        type="button"
                        disabled={!canPlayCard}
                        onClick={() => onPlayCardClick(playPayload, canPlayCard)}
                        onPointerDown={(ev) => onPlayPointerDown(ev, playPayload, canPlayCard)}
                        onPointerMove={onPlayPointerMove}
                        onPointerUp={onPlayPointerUp}
                        onPointerCancel={onPlayPointerCancel}
                        onMouseEnter={() => setHoveredCardIndex(idx)}
                        onMouseLeave={() => setHoveredCardIndex(null)}
                        onFocus={() => setHoveredCardIndex(idx)}
                        onBlur={() => setHoveredCardIndex(null)}
                        className={cn(
                          'absolute bottom-0 left-1/2 appearance-none border-0 bg-transparent p-0 transition-[transform,filter] focus:outline-none touch-none select-none',
                          dragPlayCard ? 'duration-0' : 'duration-300',
                          canPlayCard ? 'cursor-pointer' : 'cursor-not-allowed opacity-65',
                        )}
                        style={{
                          zIndex: hoveredCardIndex === idx ? 200 : baseZIndex,
                          transform: tf,
                        }}
                      >
                        {isPendingHidden ? (
                          <div className="h-[5.5rem] w-16 rounded-md border border-cyan-300/25 bg-cyan-500/10 opacity-35" />
                        ) : (
                          <PlayingCard
                            suit={suit}
                            rank={rank}
                            size="md"
                            highlighted={handLegal && canPlay}
                            className={cn(
                              canPlayCard &&
                                'ring-1 ring-neon-gold/55 shadow-[0_6px_22px_rgba(255,215,0,0.38)]',
                              !canPlayCard && 'grayscale-[0.1]',
                              belotCardIsTrump(contractTrump, cardId) ? belotCardColorClass(cardId, true) : '',
                            )}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {belotPhase === 'play' && hasHeroTableCards ? (
          <div className="mx-auto mt-2 w-full max-w-xl overflow-hidden rounded-xl border border-border/60 bg-card/55 shadow-inner">
            <div className="px-2 py-2">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-bold text-foreground/90">Ваш стол</span>
              </div>
              <div className="flex justify-center gap-1">
                {(myTableStacks || []).map((stack, colIdx) => {
                  const depth = stack?.length || 0;
                  const top = depth ? stack[depth - 1] : null;
                  const bottom = depth >= 2 ? stack[0] : null;

                  const deferHere = heroDeferActive && heroDeferActive.col === colIdx;
                  /** Во время flip новая верхняя рисуется только в верхнем слоте — не дублируем внизу. */
                  const renderBottom =
                    deferHere && heroDeferActive
                      ? null
                      : depth >= 2 && bottom != null
                        ? bottom
                        : null;
                  const showRealTop = !deferHere && top != null;
                  const topLegal = top != null && Boolean(legalSet?.has(top));
                  const canPlayCol = Boolean(canPlay && top != null && topLegal);
                  const tablePayload =
                    top != null
                      ? {
                          card: top,
                          from: /** @type {const} */ ('table'),
                          columnIndex: colIdx,
                        }
                      : null;
                  const isPendingTableTop =
                    pendingHiddenPlay?.from === 'table' &&
                    pendingHiddenPlay.columnIndex === colIdx &&
                    top != null &&
                    pendingHiddenPlay.card === top;

                  return (
                    <button
                      type="button"
                      key={`my-col-collapsed-${colIdx}`}
                      ref={tableColumnRefs[colIdx]}
                      disabled={!canPlayCol}
                      onClick={() => tablePayload && onPlayCardClick(tablePayload, canPlayCol)}
                      onPointerDown={(ev) =>
                        tablePayload && onPlayPointerDown(ev, tablePayload, canPlayCol)
                      }
                      onPointerMove={onPlayPointerMove}
                      onPointerUp={onPlayPointerUp}
                      onPointerCancel={onPlayPointerCancel}
                      className={cn(
                        'relative flex h-[84px] w-[36px] flex-col justify-end sm:h-[92px] sm:w-[40px]',
                        topLegal &&
                          canPlay &&
                          'rounded-md ring-2 ring-amber-300/50 ring-offset-1 ring-offset-background',
                      )}
                    >
                      {renderBottom != null ? (
                        <PlayingCard
                          {...belotCardIdToPlaying(renderBottom)}
                          size="sm"
                          className={cn(
                            'absolute bottom-0 left-0 z-[1] shadow-md',
                            belotCardIsTrump(contractTrump, renderBottom) ? belotCardColorClass(renderBottom, true) : '',
                          )}
                        />
                      ) : null}
                      {deferHere && heroColDefer ? (
                        prefersReducedMotion ? (
                          <PlayingCard
                            {...belotCardIdToPlaying(heroColDefer.bottom)}
                            size="sm"
                            highlighted={Boolean(legalSet?.has(heroColDefer.bottom))}
                            className={cn(
                              'relative z-[3] shadow-lg',
                              belotCardIsTrump(contractTrump, heroColDefer.bottom)
                                ? belotCardColorClass(heroColDefer.bottom, true)
                                : '',
                              canPlay ? 'cursor-pointer' : 'cursor-default opacity-80',
                            )}
                          />
                        ) : (
                          <div
                            className="relative z-[3] flex h-[3.25rem] w-10 items-end justify-center sm:h-14 sm:w-11"
                            style={{ perspective: '560px' }}
                          >
                            <div
                              className="open-duell-column-reveal w-full"
                              style={{
                                animationDuration: `${OPEN_DUELL_COLUMN_DEFER_MS}ms`,
                                transformStyle: 'preserve-3d',
                              }}
                            >
                              <PlayingCard
                                {...belotCardIdToPlaying(heroColDefer.bottom)}
                                size="sm"
                                highlighted={Boolean(legalSet?.has(heroColDefer.bottom))}
                                className={cn(
                                  'shadow-lg',
                                  belotCardIsTrump(contractTrump, heroColDefer.bottom)
                                    ? belotCardColorClass(heroColDefer.bottom, true)
                                    : '',
                                  canPlay ? 'cursor-pointer' : 'cursor-default opacity-80',
                                )}
                              />
                            </div>
                          </div>
                        )
                      ) : showRealTop ? (
                        isPendingTableTop ? (
                          <div className="relative z-[3] h-[3.25rem] w-10 rounded border border-cyan-300/25 bg-cyan-500/10 opacity-35 shadow-lg sm:h-14 sm:w-11" />
                        ) : (
                          <PlayingCard
                            {...belotCardIdToPlaying(top)}
                            size="sm"
                            highlighted={topLegal}
                            className={cn(
                              'relative z-[3] shadow-lg transition-transform hover:z-[25] hover:-translate-y-0.5',
                              belotCardIsTrump(contractTrump, top) ? belotCardColorClass(top, true) : '',
                              canPlay ? 'cursor-pointer' : 'cursor-default opacity-80',
                            )}
                          />
                        )
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}

      </div>

      {cardFly ? (
        <div ref={flyCardWrapRef} className="pointer-events-none fixed z-[200]" aria-hidden>
          <PlayingCard rank={cardFly.rank} suit={cardFly.suit} size="md" className="shadow-2xl" />
        </div>
      ) : null}
      {dragPlayCard ? (
        <div
          ref={dragGhostRef}
          className="pointer-events-none fixed z-[205]"
          style={{
            left: dragPlayCard.x,
            top: dragPlayCard.y,
            transform: `translate(-50%, -64%) rotate(5deg) scale(${dragPlayCard.returning ? 1 : 1.04})`,
            filter: dragPlayCard.dropActive
              ? 'drop-shadow(0 0 24px rgba(34,211,238,0.55))'
              : 'drop-shadow(0 0 18px rgba(0,0,0,0.45))',
          }}
          aria-hidden
        >
          <PlayingCard
            rank={dragPlayCard.rank}
            suit={dragPlayCard.suit}
            size="md"
            className={cn(
              'shadow-2xl transition-all duration-75',
              dragPlayCard.dropActive && 'ring-2 ring-cyan-300/80 ring-offset-2 ring-offset-black/25',
            )}
          />
        </div>
      ) : null}

      {bottomActions ? bottomActions : null}
    </div>
  );
}
